import React, { useCallback } from 'react';
import { useAsyncValue, useNavigate, useRevalidator } from 'react-router-dom';
import { useRevalidateOnSync } from '../../../../offline/useRevalidateOnSync';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Components Styled
import { Container, ContainerButton } from '../components/MainStyledComponents';

// Components
import { CheckBoxTagTable } from '../../../../components/display/tables/CheckBoxTagTable';
import { MainButton } from '../../../../components/inputs/buttons/MainButton';

// Types
import { OrderType } from '../../../../types/orders';
import {
  tableIds,
  takeBase,
} from '../../../../components/display/tables/types';
import { firstPageTake } from '../../../../utils/cursor-take.helper';
import { dataTypes } from '../../../../components/display/tables/CheckBoxTagTable/types';

// Utils
import { modalIds } from '../../../../components/modals/SuperModal/types';
import { openModal } from '../../../../state/actions/ui/modal';

// Hooks
import { usePersistentSelection } from '../../../../hooks/usePersistentSelection';
import { usePollingRevalidation } from '../../../../hooks/usePollingRevalidation';

// GQL
import { client } from '../../../../gql/client';
import { GET_ORDER_BY_ID } from '../../../../gql/queries/orders/orders';
import { GET_GENERIC_ORDERS } from '../../../../gql/queries/genericOrders';
import { selectAllOrders } from '../../../../gql/selectors/orders';

// Redux
import { getCursor } from '../../../../state/selectors/ui/tables';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { store } from '../../../../state/configureStore';
import * as Sentry from '@sentry/react';
import { withNavigationFeedback } from '../../../../utils/navigationFeedback';

export default function SearchAndCreateHistoricalOrderPage(): JSX.Element {
  const { t } = useTranslation(['workOrders', 'pages']);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const data = useAsyncValue() as dataTypes;
  const revalidator = useRevalidator();
  useRevalidateOnSync();
  // Persistent selection — all state managed by the hook
  const {
    rowSelection,
    onRowSelectionChange,
    selectedCount,
    selectAllMode,
    excludedIds,
    selectedRows,
    handleSelectAll,
    handleClearSelection,
  } = usePersistentSelection({
    dataAll: data.genericOrders || [],
    totalCount: data.totalCount || 0,
  });

  const handleSeeWorkOrder = useCallback(() => {
    const firstSelected = selectedRows[0];
    const tasks = () => withNavigationFeedback(async () => {
      if (firstSelected) {
        const {
          data: { order },
        } = await client.query({
          query: GET_ORDER_BY_ID,
          variables: {
            id: firstSelected,
          },
        });

        navigate(`summary/${order.id}`);
      }
    });
    tasks().catch((err) => {
      Sentry.captureException(err);
      console.log(err);
    });
  }, [selectedRows]);

  const onReFetchData = useCallback(async () => {
    // Always read from store.getState() — avoids stale closure
    const currentCursor = getCursor(tableIds.SEARCH_AND_CREATE_HISTORICAL_WORKORDERS)(store.getState());
    // Tarea #11: respetar el take guardado en cursor (page-size selector).
    const effectiveTake = firstPageTake(currentCursor.take, takeBase);
    const { data } = await client.query({
      query: GET_GENERIC_ORDERS,
      variables: {
        input: {
          take: effectiveTake,
          cursor: '',
          sortField: currentCursor.sortField,
          orderBy: currentCursor.orderBy ?? 'desc',
          concessionId: currentCursor.concessionId,
          teamIds: currentCursor.teamIds ?? [],
          toDate: currentCursor.toDate,
          fromDate: currentCursor.fromDate,
          indexType: currentCursor.indexType,
          indexExpedition: currentCursor.indexExpedition,
          classification: currentCursor.classification,
          predefinedOrderId: currentCursor.predefinedOrderId,
          capitolId: currentCursor.capitolId,
          subCapitolId: currentCursor.subCapitolId,
          states: currentCursor.states,
          pkInitKm: currentCursor.pkInitKm,
          pkInitMeter: currentCursor.pkInitMeter,
          pkEndKm: currentCursor.pkEndKm,
          pkEndMeter: currentCursor.pkEndMeter,
          track: currentCursor.track,
          // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
          onlyFlagged: currentCursor.onlyFlagged,
        },
      },
      fetchPolicy: 'network-only',
    });

    const setCursorData = {
      take: effectiveTake,
      cursor: '',
      sortField: currentCursor?.sortField,
      orderBy: currentCursor?.orderBy ?? 'desc',
      concessionId: currentCursor?.concessionId,
      teamIds: currentCursor?.teamIds ?? [],
      toDate: currentCursor?.toDate,
      fromDate: currentCursor?.fromDate,
      indexType: currentCursor?.indexType,
      indexExpedition: currentCursor?.indexExpedition,
      classification: currentCursor?.classification,
      predefinedOrderId: currentCursor?.predefinedOrderId,
      capitolId: currentCursor?.capitolId,
      subCapitolId: currentCursor?.subCapitolId,
      states: currentCursor?.states,
      pkInitKm: currentCursor?.pkInitKm,
      pkInitMeter: currentCursor?.pkInitMeter,
      pkEndKm: currentCursor?.pkEndKm,
      pkEndMeter: currentCursor?.pkEndMeter,
      track: currentCursor?.track,
      onlyFlagged: currentCursor?.onlyFlagged,
    };

    dispatch(
      setCursor(tableIds.SEARCH_AND_CREATE_HISTORICAL_WORKORDERS, setCursorData)
    );

    await selectAllOrders(data.genericOrders.genericOrders);

    revalidator.revalidate();
  }, []);

  const handleDownloadHistory = useCallback(() => {
    const currentCursor = getCursor(tableIds.SEARCH_AND_CREATE_HISTORICAL_WORKORDERS)(store.getState());
    const { id: _id, take: _take, cursor: _cursor, ...filterFields } = currentCursor || {} as any;
    dispatch(
      openModal(modalIds.DOWNLOAD_HISTORIC, {
        type: OrderType.Generic,
        entityType: 'generic',
        showCombinedReport: true,
        filterPositionColumn: 2,
        selectedRows: selectAllMode ? [] : selectedRows,
        selectAll: selectAllMode,
        excludedIds: selectAllMode ? [...excludedIds] : [],
        filters: filterFields,
        totalCount: data.totalCount || 0,
      })
    );
  }, [selectedRows, dispatch, selectAllMode, excludedIds, data.totalCount]);

  const onRefetchPagination = useCallback(async () => {
    revalidator.revalidate();
  }, []);

  // Polling: refresca datos de pág 1 en cache (network-only) sin cambiar posición del usuario
  const onPollingRefresh = useCallback(async () => {
    const currentCursor = getCursor(tableIds.SEARCH_AND_CREATE_HISTORICAL_WORKORDERS)(store.getState());
    const effectiveTake = firstPageTake(currentCursor.take, takeBase);
    await client.query({
      query: GET_GENERIC_ORDERS,
      variables: {
        input: {
          take: effectiveTake,
          cursor: '',
          sortField: currentCursor.sortField,
          orderBy: currentCursor.orderBy ?? 'desc',
          concessionId: currentCursor.concessionId,
          teamIds: currentCursor.teamIds ?? [],
          toDate: currentCursor.toDate,
          fromDate: currentCursor.fromDate,
          indexType: currentCursor.indexType,
          indexExpedition: currentCursor.indexExpedition,
          classification: currentCursor.classification,
          predefinedOrderId: currentCursor.predefinedOrderId,
          capitolId: currentCursor.capitolId,
          subCapitolId: currentCursor.subCapitolId,
          states: currentCursor.states,
          pkInitKm: currentCursor.pkInitKm,
          pkInitMeter: currentCursor.pkInitMeter,
          pkEndKm: currentCursor.pkEndKm,
          pkEndMeter: currentCursor.pkEndMeter,
          track: currentCursor.track,
          // Sin esto la lista filtrada dejaría de refrescarse en silencio.
          onlyFlagged: currentCursor.onlyFlagged,
        },
      },
      fetchPolicy: 'network-only',
    });
    revalidator.revalidate();
  }, []);
  usePollingRevalidation(onPollingRefresh);

  return (
    <Container>
      <CheckBoxTagTable
        idCss={'historical-' + OrderType.Generic}
        titleText={t('pages:workOrders.filterBy')}
        data={data}
        onReFetchData={onReFetchData}
        onReFetchPagination={onRefetchPagination}
        tableId={tableIds.SEARCH_AND_CREATE_HISTORICAL_WORKORDERS}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        selectAllMode={selectAllMode}
        excludedIds={excludedIds}
        onSelectAll={handleSelectAll}
        onClearSelection={handleClearSelection}
        selectedCount={selectedCount}
        // Banderitas (spec 2026-10-22 § 8): columna y filtro «Solo marcadas».
        // La fila ya lleva el id de la Order (`selectAllOrders`).
        showFlagColumn
        showOnlyFlaggedFilter
      />
      <ContainerButton>
        <MainButton
          variant="secondary"
          text={t('pages:workOrders.view')}
          disabled={selectedCount !== 1}
          onClick={handleSeeWorkOrder}
        />
        <MainButton
          text={t('pages:workOrders.downloadHistoric')}
          disabled={selectedCount === 0}
          onClick={handleDownloadHistory}
        />
      </ContainerButton>
    </Container>
  );
}
